import { App, DatePicker, Form, Select, Space } from 'antd';
import type { Dayjs } from 'dayjs';
import { Plus } from 'lucide-react';
import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { crestHttp } from '../../api/client.js';
import { FancyModal } from '../../components/ui/index.js';
import {
  AccessScopeSelector,
  type ScopePolicy,
  defaultAccessScopeConfig,
  hasInvalidSelectedScope,
} from './AccessScopeSelector.js';
import type { RoleCatalogRow, UserOption } from './access-center-types.js';

interface DirectGrantForm {
  userId: string;
  roleCatalogId: string;
  validFrom?: Dayjs;
  validTo?: Dayjs;
}

interface Props {
  open: boolean;
  roles: RoleCatalogRow[];
  users: UserOption[];
  userId?: string;
  onCancel: () => void;
  onCreated: () => void;
}

export function DirectAccessGrantModal({
  open,
  roles,
  users,
  userId,
  onCancel,
  onCreated,
}: Props) {
  const { t } = useTranslation('accessCenter');
  const { message } = App.useApp();
  const [form] = Form.useForm<DirectGrantForm>();
  const roleId = Form.useWatch('roleCatalogId', form);
  const [scopePolicy, setScopePolicy] = useState<ScopePolicy>('role_default');
  const [scopeConfig, setScopeConfig] = useState(defaultAccessScopeConfig);
  const role = useMemo(
    () => roles.find((item) => item.role_catalog_id === roleId),
    [roleId, roles],
  );

  useEffect(() => {
    if (open && userId) form.setFieldValue('userId', userId);
  }, [form, open, userId]);

  const submit = async () => {
    const values = await form.validateFields();
    if (scopePolicy === 'explicit' && hasInvalidSelectedScope(scopeConfig)) {
      message.error(t('scopeRequired'));
      return;
    }
    await crestHttp.post('/admin/access/assignments/direct', {
      userId: values.userId,
      roleCatalogId: values.roleCatalogId,
      scopePolicy: role?.domain === 'crm' ? scopePolicy : 'role_default',
      scopeConfig: role?.domain === 'crm' && scopePolicy === 'explicit' ? scopeConfig : {},
      validFrom: values.validFrom?.toISOString(),
      validTo: values.validTo?.toISOString(),
    });
    message.success(t('grantCreated'));
    form.resetFields();
    resetScope();
    onCreated();
  };

  const resetScope = () => {
    setScopePolicy('role_default');
    setScopeConfig(defaultAccessScopeConfig());
  };

  return (
    <FancyModal
      title={t('directGrant')}
      subtitle={t('directGrantSubtitle')}
      icon={<Plus size={18} />}
      open={open}
      onCancel={() => {
        form.resetFields();
        resetScope();
        onCancel();
      }}
      onOk={submit}
    >
      <Form form={form} layout="vertical">
        <Form.Item name="userId" label={t('user')} rules={[{ required: true }]}>
          <Select
            showSearch
            disabled={Boolean(userId)}
            optionFilterProp="label"
            options={users.map((user) => ({
              value: user.systemuserid,
              label: `${user.fullname} · ${user.domainname ?? ''}`,
            }))}
          />
        </Form.Item>
        <Form.Item name="roleCatalogId" label={t('roleName')} rules={[{ required: true }]}>
          <Select
            showSearch
            optionFilterProp="label"
            options={roles.map((item) => ({
              value: item.role_catalog_id,
              label: `[${item.domain.toUpperCase()}] ${item.name}`,
            }))}
          />
        </Form.Item>
        <AccessScopeSelector
          role={role}
          mode="grant"
          scopePolicy={scopePolicy}
          scopeConfig={scopeConfig}
          onPolicyChange={(value) => {
            setScopePolicy(value);
            setScopeConfig(defaultAccessScopeConfig());
          }}
          onScopeConfigChange={setScopeConfig}
        />
        <Space align="start">
          <Form.Item name="validFrom" label={t('validFrom')}>
            <DatePicker showTime />
          </Form.Item>
          <Form.Item name="validTo" label={t('validTo')}>
            <DatePicker showTime />
          </Form.Item>
        </Space>
      </Form>
    </FancyModal>
  );
}
